import { useLocation } from 'react-router-dom'

import {
  parseQueryParams,
  serializeQueryParams,
} from '~publish/hooks/useQueryParam'
import { communityChannel, communityPage } from '~publish/legacy/routes'

import { filterQueryParamsByRoute } from './utils/filterQueryParamsByRoute'
import { getCurrentCommunityView } from './utils/getCurrentCommunityView'

/**
 * Build main community page URL with preserved filter params (status, channels)
 *
 * This hook ensures that when navigating to the main Community page (new IA overhaul),
 * the selected filters (status, channel filters) persist in the URL.
 * It also preserves the current view (list vs by-post) so that clicking
 * the Community nav link returns to whichever view the user last selected.
 *
 * When a singleChannelId is provided, it returns a channel-specific community URL
 * instead of the aggregated community view.
 *
 * @param singleChannelId - Optional channel ID to link directly to a specific channel's community
 * @returns URL string with preserved query parameters
 *
 * @example
 * ```tsx
 * const commentsUrl = useCommentsUrl()
 * // If current URL has ?status=dismissed&channels[]=123, returns: /community?status=dismissed&channels[]=123
 *
 * const singleChannelUrl = useCommentsUrl('channel-123')
 * // Returns: /channels/channel-123/community
 * ```
 */
export function useCommentsUrl(singleChannelId?: string): string {
  const location = useLocation()
  const currentSearch = parseQueryParams(location.search)
  const currentView = getCurrentCommunityView(location.pathname)

  // When a single channel is provided, link directly to that channel's community
  if (singleChannelId) {
    const filteredParams = filterQueryParamsByRoute(
      currentSearch,
      communityChannel.queryParams,
    )
    const queryString = serializeQueryParams(filteredParams)
    const viewSegment = currentView === 'comments' ? '' : '/posts'
    return `/channels/${singleChannelId}/community${viewSegment}${
      queryString ? `?${queryString}` : ''
    }`
  }

  // Filter current search params to only include those valid for community page routes
  const filteredParams = filterQueryParamsByRoute(
    currentSearch,
    communityPage.queryParams,
  )

  const queryString = serializeQueryParams(filteredParams)
  return (
    communityPage.getViewRoute({
      view: currentView,
      copiedSearchParams: [],
    }) + (queryString ? `?${queryString}` : '')
  )
}
